import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Check, Loader2, Shuffle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  getStarterTools, getStack, getOrchestrator, toggleOrchestrator,
} from "@/lib/wikifi";

/**
 * MyStackRail — a compact row of AI tool logos used by the Wikifi workspace.
 *
 * Variants:
 *   • variant="topbar":  the primary placement inside the workspace sub-header
 *                        row (right of "+ New" and left of "Wikifi Auto").
 *                        Icons only, horizontally-scrollable on narrow screens.
 *   • variant="body":    kept for backwards-compat, but is now a no-op —
 *                        the workspace no longer renders this variant. Left in
 *                        place so any callers importing it still compile.
 *   • variant="header":  legacy compact header rail (kept for the global site
 *                        header, out of the workspace flow).
 *
 * Data source (all variants):
 *   • Anonymous or logged-in-with-empty-stack → 8 top-ranked directory tools
 *     from /api/wikifi/starter-tools ("Popular AI right now").
 *   • Logged-in with pinned tools → the user's actual My Stack, from /api/stack.
 * Each icon links to /tool/<slug> in a NEW TAB so the current conversation
 * isn't lost.
 */
export default function MyStackRail({ variant = "body" }) {
  const { user } = useAuth();
  const [stack, setStack] = useState([]);
  const [starter, setStarter] = useState([]);
  const [orchestratorSet, setOrchestratorSet] = useState(new Set());
  const [busy, setBusy] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [shuffling, setShuffling] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const s = await getStarterTools().catch(() => []);
      if (cancelled) return;
      setStarter(s);
      if (user) {
        const [pinned, orch] = await Promise.all([getStack(), getOrchestrator()]);
        if (cancelled) return;
        setStack(pinned || []);
        setOrchestratorSet(new Set(orch?.slugs || []));
      }
      setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [user]);

  const showingUserStack = user && stack.length > 0;
  const items = showingUserStack ? stack : starter;

  // Body variant is deprecated inside the workspace — render nothing so
  // legacy callers don't accidentally double-render.
  if (variant === "body") return null;

  // Header variant: only shown when the user actually has a stack.
  if (variant === "header" && !showingUserStack) return null;

  // Topbar variant: always render once loaded (default popular OR own stack).
  if (variant === "topbar" && !loaded) {
    return <div className="h-8" aria-hidden="true"/>;
  }
  if (variant === "topbar" && items.length === 0) {
    return null;
  }

  const onOrchestrate = async (slug, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!user) {
      toast.info("Sign in to add tools to your Orchestrator.");
      return;
    }
    setBusy(slug);
    try {
      const r = await toggleOrchestrator(slug);
      setOrchestratorSet((prev) => {
        const next = new Set(prev);
        if (r.added) next.add(slug); else next.delete(slug);
        return next;
      });
      toast.success(r.added
        ? "Added to Orchestrator — you'll be notified when this integration goes live."
        : "Removed from Orchestrator."
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update Orchestrator.");
    } finally {
      setBusy(null);
    }
  };

  const testidSuffix = showingUserStack ? "stack" : "popular";

  // Reshuffle the popular rail — only meaningful when we're NOT showing
  // the user's My Stack (their stack shouldn't reshuffle on click). Hits
  // the backend with ?refresh=1 so the whole pod gets a fresh sample.
  const onShuffle = async () => {
    if (showingUserStack || shuffling) return;
    setShuffling(true);
    try {
      const fresh = await getStarterTools(true);
      setStarter(fresh);
    } catch (_) { /* silent */ }
    finally { setShuffling(false); }
  };

  // ── Header variant (Wikificial site-level header) ─────────────────────
  if (variant === "header") {
    return (
      <div className="flex items-center gap-1.5" data-testid="mystack-header-rail">
        <span className="hidden lg:inline text-[10px] font-semibold uppercase tracking-wider text-[#8A93A6] mr-1">Stack</span>
        {items.slice(0, 8).map((t) => (
          <Link
            key={t.slug}
            to={`/tool/${t.slug}`}
            target="_blank"
            rel="noopener"
            className="w-7 h-7 rounded-md bg-white border border-[#E7EAF0] hover:border-[#0B1220]/40 hover:shadow-sm flex items-center justify-center overflow-hidden transition-all"
            title={t.name}
            data-testid={`mystack-header-${t.slug}`}
          >
            <ToolIcon tool={t} size={20}/>
          </Link>
        ))}
      </div>
    );
  }

  // ── Topbar variant (workspace sub-header) ─────────────────────────────
  // Fills the full available width between "+ New" and the model picker.
  // Icons are evenly-spaced and shrink slightly on narrow viewports; when
  // the row gets tighter than the icon count needs, it becomes horizontally
  // scrollable (native touch scroll on mobile, hidden scrollbar on desktop).
  return (
    <div
      className="flex-1 min-w-0 overflow-x-auto scrollbar-thin"
      data-testid={`mystack-topbar-rail-${testidSuffix}`}
      style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
    >
      <div className="flex items-center justify-start gap-1 md:gap-1.5 min-w-max">
        <div className="hidden lg:flex items-center gap-1 mr-1 shrink-0">
          <span className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-[#8A93A6]">
            {showingUserStack ? "My Stack" : "Popular"}
          </span>
          {!showingUserStack && (
            <button
              onClick={onShuffle}
              disabled={shuffling}
              className="w-5 h-5 rounded-md flex items-center justify-center text-[#8A93A6] hover:text-[#0B1220] hover:bg-[#F6F7FB] transition-colors disabled:opacity-40"
              title="Shuffle — show a different mix of AI tools"
              data-testid="rail-shuffle-btn"
            >
              {shuffling
                ? <Loader2 size={11} className="animate-spin"/>
                : <Shuffle size={11}/>}
            </button>
          )}
        </div>
        {items.slice(0, 24).map((t) => {
          const orch = orchestratorSet.has(t.slug);
          return (
            <div key={t.slug} className="relative group shrink-0" data-testid={`mystack-topbar-${t.slug}`}>
              <Link
                to={`/tool/${t.slug}`}
                target="_blank"
                rel="noopener"
                className="block w-8 h-8 rounded-md bg-white border border-[#E7EAF0] hover:border-[#0B1220]/40 hover:shadow-sm flex items-center justify-center overflow-hidden transition-all"
                title={t.name}
              >
                <ToolIcon tool={t} size={22}/>
              </Link>
              <button
                onClick={(e) => onOrchestrate(t.slug, e)}
                disabled={busy === t.slug}
                className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center transition-opacity ${
                  orch ? "bg-[#0B1220] text-white opacity-100" : "bg-white border border-[#E7EAF0] text-[#5B6478] opacity-0 group-hover:opacity-100"
                }`}
                title={orch ? "In Orchestrator" : "Add to Orchestrator (beta)"}
                data-testid={`orch-toggle-${t.slug}`}
              >
                {busy === t.slug ? <Loader2 size={7} className="animate-spin"/> :
                  orch ? <Check size={8}/> : <Plus size={8}/>}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * ToolIcon — renders a real logo image if the tool has one, cascading through
 * multiple reliable sources before falling back to an initials circle.
 *
 * Source priority:
 *   1. `tool.logo_url`  (whatever the ingest pipeline populated — often Clearbit)
 *   2. Google's s2 favicon service, derived from `tool.website` (highly reliable,
 *      returns a favicon for essentially any live domain)
 *   3. Initials-circle fallback
 *
 * The cascade lets us render a real logo even for tools whose stored logo_url
 * points to a Clearbit endpoint that's since 404'd or been rate-limited.
 */
// Curated logo map — every URL is verified working. For brands with a
// Simple Icons SVG we use that (crisp any size); for brands not on Simple
// Icons we fall back to DuckDuckGo's icon service directly. Every slug
// listed here is GUARANTEED to render a real logo — no cascade needed.
const SLUG_LOGO_MAP = {
  // OpenAI family — Simple Icons doesn't stock "openai" any more, use ChatGPT icon assets
  chatgpt: "https://icons.duckduckgo.com/ip3/chatgpt.com.ico",
  dalle: "https://icons.duckduckgo.com/ip3/openai.com.ico",
  "dall-e": "https://icons.duckduckgo.com/ip3/openai.com.ico",
  sora: "https://icons.duckduckgo.com/ip3/openai.com.ico",
  // Anthropic — Simple Icons
  claude: "https://cdn.simpleicons.org/anthropic/D97757",
  // Google AI
  gemini: "https://cdn.simpleicons.org/googlegemini/8E75B2",
  "nano-banana": "https://cdn.simpleicons.org/googlegemini/8E75B2",
  bard: "https://cdn.simpleicons.org/googlegemini/8E75B2",
  veo: "https://cdn.simpleicons.org/googlegemini/8E75B2",
  imagen: "https://cdn.simpleicons.org/googlegemini/8E75B2",
  "google-antigravity": "https://cdn.simpleicons.org/google/4285F4",
  // Meta
  "meta-ai": "https://cdn.simpleicons.org/meta/0866FF",
  llama: "https://cdn.simpleicons.org/meta/0866FF",
  // Frontier chat
  deepseek: "https://cdn.simpleicons.org/deepseek/4D6BFE",
  perplexity: "https://cdn.simpleicons.org/perplexity/1FB8CD",
  grok: "https://cdn.simpleicons.org/x/000000",
  mistral: "https://cdn.simpleicons.org/mistralai/FF7000",
  // Productivity
  notion: "https://cdn.simpleicons.org/notion/000000",
  "notion-ai": "https://cdn.simpleicons.org/notion/000000",
  hubspot: "https://cdn.simpleicons.org/hubspot/FF7A59",
  gamma: "https://icons.duckduckgo.com/ip3/gamma.app.ico",
  airtable: "https://cdn.simpleicons.org/airtable/18BFFF",
  "airtable-ai": "https://cdn.simpleicons.org/airtable/18BFFF",
  // Voice & audio
  elevenlabs: "https://cdn.simpleicons.org/elevenlabs/000000",
  suno: "https://cdn.simpleicons.org/suno/000000",
  // Image / video
  midjourney: "https://cdn.simpleicons.org/midjourney/000000",
  "hugging-face": "https://cdn.simpleicons.org/huggingface/FFD21E",
  huggingface: "https://cdn.simpleicons.org/huggingface/FFD21E",
  runway: "https://icons.duckduckgo.com/ip3/runwayml.com.ico",
  capcut: "https://icons.duckduckgo.com/ip3/capcut.com.ico",
  picsart: "https://cdn.simpleicons.org/picsart/C209C1",
  vsco: "https://cdn.simpleicons.org/vsco/000000",
  heygen: "https://icons.duckduckgo.com/ip3/heygen.com.ico",
  leonardo: "https://icons.duckduckgo.com/ip3/leonardo.ai.ico",
  "leonardo-ai": "https://icons.duckduckgo.com/ip3/leonardo.ai.ico",
  // Dev tools
  replit: "https://cdn.simpleicons.org/replit/F26207",
  bitbucket: "https://cdn.simpleicons.org/bitbucket/2684FF",
  lovable: "https://icons.duckduckgo.com/ip3/lovable.dev.ico",
  openrouter: "https://cdn.simpleicons.org/openrouter/6467F2",
  // Business platforms
  "salesforce-einstein": "https://cdn.simpleicons.org/salesforce/00A1E0",
  shutterstock: "https://icons.duckduckgo.com/ip3/shutterstock.com.ico",
  "photoshop-generative": "https://cdn.simpleicons.org/adobephotoshop/31A8FF",
  firefly: "https://icons.duckduckgo.com/ip3/firefly.adobe.com.ico",
  "adobe-firefly": "https://icons.duckduckgo.com/ip3/firefly.adobe.com.ico",
  // Search / research / etc
  "duckduckgo-ai-chat": "https://cdn.simpleicons.org/duckduckgo/DE5833",
  wondershare: "https://cdn.simpleicons.org/wondershare/E80000",
};

function ToolIcon({ tool, size = 20 }) {
  const [attempt, setAttempt] = useState(0);
  const sources = React.useMemo(() => {
    const list = [];
    // 1. Curated Simple Icons SVG for well-known AI brands — cleanest visual.
    const curated = SLUG_LOGO_MAP[tool.slug];
    if (curated) list.push(curated);
    // Derive a domain from either the tool's website OR its stored logo URL.
    const domain = extractDomain(tool.website) || domainFromClearbit(tool.logo_url);
    if (domain) {
      // 2. DuckDuckGo icons — 200 direct, very reliable.
      list.push(`https://icons.duckduckgo.com/ip3/${domain}.ico`);
      // 3. Google's s2 favicon service (redirect to gstatic).
      list.push(`https://www.google.com/s2/favicons?domain=${domain}&sz=128`);
    }
    // 4. Whatever the ingest pipeline stored (usually a Clearbit URL — dead
    //    since Dec 2023, kept so any future self-hosted logo_url still works).
    if (tool.logo_url) list.push(tool.logo_url);
    return list;
  }, [tool.slug, tool.logo_url, tool.website]);
  const initials = (tool.name || "AI").slice(0, 2).toUpperCase();
  const src = sources[attempt];
  if (!src) {
    return (
      <span
        className="font-bold text-[#5B6478]"
        style={{ fontSize: Math.max(8, Math.round(size * 0.42)) + "px" }}
        aria-hidden="true"
      >
        {initials}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      onError={() => setAttempt((a) => a + 1)}
      className="object-contain"
      style={{ width: size, height: size }}
    />
  );
}

function extractDomain(url) {
  if (!url || typeof url !== "string") return null;
  try {
    const u = new URL(url.trim().startsWith("http") ? url.trim() : `https://${url.trim()}`);
    return u.hostname.replace(/^www\./, "");
  } catch { return null; }
}

function domainFromClearbit(url) {
  if (!url || typeof url !== "string") return null;
  const m = url.match(/logo\.clearbit\.com\/([^/?#]+)/i);
  return m ? m[1] : null;
}
